import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { useT } from "../context/LanguageContext";
import { X, Trophy, AlertCircle, Calendar, Loader2, MessageSquareOff } from "lucide-react";

/**
 * Admin-only read-only DM viewer used by the Non-Responding tab.
 * Shows the conversation between the two players involved in a match,
 * highlighting messages sent on the match's scheduled date so the admin
 * can verify the chat-silence forfeit was awarded to the correct player
 * (i.e., the one who actually messaged on game day).
 *
 * Uses GET /api/admin/dm-thread (mod / admin only).
 */
export default function NrChatModal({ row, onClose }) {
  const { t } = useT();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  const winnerId = row?.winner?.id;
  const loserId = row?.loser?.id;
  const matchId = row?.match_id;

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  useEffect(() => {
    if (!winnerId || !loserId) {
      setLoading(false);
      setErr(t("nr.chat.errMissingPlayers"));
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({ user_a: winnerId, user_b: loserId });
        if (matchId) params.set("match_id", matchId);
        const { data } = await api.get(`/admin/dm-thread?${params.toString()}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled) setErr(formatApiError(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [winnerId, loserId, matchId, t]);

  const matchDay = data?.match?.match_day;
  const winnerName = row?.winner?.username || "—";
  const loserName = row?.loser?.username || "—";
  const counts = data?.counts || { a_total: 0, b_total: 0, a_match_day: 0, b_match_day: 0 };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      data-testid="nr-chat-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="w-full max-w-2xl max-h-[90vh] bg-[#FFFFFF] dark:bg-[#0F0F14] border border-[#E4E4E7] dark:border-[#2A2A30] rounded-lg flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#E4E4E7] dark:border-[#2A2A30] bg-[#FAFAFC] dark:bg-[#0A0A0F]">
          <div className="min-w-0">
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#71717A]">{t("nr.chat.kicker")}</div>
            <div className="font-heading text-lg uppercase tracking-wider text-[#09090B] dark:text-[#F4F4F5] mt-0.5 truncate">
              {winnerName} <span className="text-[#71717A] mx-1">vs</span> {loserName}
            </div>
            {matchDay && (
              <div className="flex items-center gap-1.5 mt-1 text-[11px] font-mono text-[#71717A]">
                <Calendar className="w-3 h-3" /> {t("nr.chat.matchDay")}: <span className="text-[#7C3AED] font-bold">{matchDay}</span>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            data-testid="nr-chat-close"
            className="flex-shrink-0 text-[#71717A] hover:text-[#09090B] dark:hover:text-white p-1"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Player counters */}
        <div className="grid grid-cols-2 gap-px bg-[#E4E4E7] dark:bg-[#2A2A30] flex-shrink-0" data-testid="nr-chat-counters">
          <CounterCard
            kind="winner"
            name={winnerName}
            total={counts.a_total}
            matchDay={counts.a_match_day}
            t={t}
          />
          <CounterCard
            kind="loser"
            name={loserName}
            total={counts.b_total}
            matchDay={counts.b_match_day}
            t={t}
          />
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4 bg-[#FFFFFF] dark:bg-[#0F0F14]" data-testid="nr-chat-body">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-[#71717A] font-mono text-xs uppercase tracking-widest">
              <Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("nr.chat.loading")}
            </div>
          ) : err ? (
            <div className="flex items-start gap-2 text-[#E11D48] font-mono text-xs py-6" data-testid="nr-chat-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
              <div>{err}</div>
            </div>
          ) : !data?.messages?.length ? (
            <div className="flex flex-col items-center justify-center py-12 text-center text-[#71717A]" data-testid="nr-chat-empty">
              <MessageSquareOff className="w-8 h-8 mb-2" />
              <div className="font-mono text-xs uppercase tracking-widest">{t("nr.chat.empty")}</div>
              <p className="text-xs mt-2 max-w-sm">{t("nr.chat.emptyHelp")}</p>
            </div>
          ) : (
            <ul className="space-y-3">
              {data.messages.map((m) => {
                const fromWinner = m.from_id === winnerId;
                const isMatchDay = !!m.is_match_day;
                return (
                  <li
                    key={m.id}
                    className={`flex ${fromWinner ? "justify-start" : "justify-end"}`}
                    data-testid={`nr-chat-msg-${m.id}`}
                    data-is-match-day={isMatchDay ? "true" : "false"}
                  >
                    <div className={`max-w-[78%] rounded-2xl px-3.5 py-2 text-sm shadow-sm ${
                      fromWinner
                        ? "bg-[#00FF87]/10 border border-[#00FF87]/30 text-[#09090B] dark:text-[#F4F4F5]"
                        : "bg-[#7C3AED]/10 border border-[#7C3AED]/30 text-[#09090B] dark:text-[#F4F4F5]"
                    } ${isMatchDay ? "ring-2 ring-[#FFD700] ring-offset-2 ring-offset-[#FFFFFF] dark:ring-offset-[#0F0F14]" : ""}`}>
                      <div className="flex items-center gap-1.5 mb-1">
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${fromWinner ? "text-[#00FF87]" : "text-[#7C3AED]"}`}>
                          {m.from_username || (fromWinner ? winnerName : loserName)}
                        </span>
                        {isMatchDay && (
                          <span className="text-[8px] font-bold uppercase tracking-widest bg-[#FFD700] text-[#09090B] px-1.5 py-0.5 rounded-full" data-testid="nr-chat-badge-matchday">
                            {t("nr.chat.matchDayBadge")}
                          </span>
                        )}
                      </div>
                      <div className="whitespace-pre-wrap break-words">{m.text}</div>
                      <div className="text-[10px] text-[#71717A] mt-1 font-mono">
                        {fmtTs(m.created_at)}
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Footer / hint */}
        <div className="px-5 py-3 border-t border-[#E4E4E7] dark:border-[#2A2A30] bg-[#FAFAFC] dark:bg-[#0A0A0F] text-[10px] font-mono uppercase tracking-[0.25em] text-[#71717A] flex flex-wrap items-center gap-3" data-testid="nr-chat-legend">
          <span className="inline-flex items-center gap-1.5">
            <Trophy className="w-3 h-3 text-[#00FF87]" /> {t("nr.chat.legend.winner")}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <AlertCircle className="w-3 h-3 text-[#E11D48]" /> {t("nr.chat.legend.loser")}
          </span>
          <span className="inline-flex items-center gap-1.5 ml-auto">
            <span className="w-2.5 h-2.5 bg-[#FFD700] rounded-full" /> {t("nr.chat.legend.matchDay")}
          </span>
        </div>
      </div>
    </div>
  );
}

function CounterCard({ kind, name, total, matchDay, t }) {
  const accent = kind === "winner" ? "#00FF87" : "#E11D48";
  const Icon = kind === "winner" ? Trophy : AlertCircle;
  return (
    <div className="bg-[#FFFFFF] dark:bg-[#0F0F14] px-4 py-3" data-testid={`nr-chat-counter-${kind}`}>
      <div className="flex items-center gap-1.5 mb-1">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#71717A]">
          {kind === "winner" ? t("nr.chat.counter.winner") : t("nr.chat.counter.loser")}
        </span>
      </div>
      <div className="font-heading text-base text-[#09090B] dark:text-[#F4F4F5] truncate">{name}</div>
      <div className="mt-1 flex items-baseline gap-2">
        <div>
          <div className="font-score text-xl" style={{ color: accent }}>{matchDay}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{t("nr.chat.counter.onDay")}</div>
        </div>
        <div className="text-[#71717A] text-xs">/</div>
        <div>
          <div className="font-score text-base text-[#71717A]">{total}</div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#71717A]">{t("nr.chat.counter.total")}</div>
        </div>
      </div>
    </div>
  );
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
}
